// ABOUTME: Hook that resolves last-edit metadata (date + author) for the current entity/scope.
// ABOUTME: Fetches config for any entity type and resolves the user display name from _meta.

import {useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {useUser} from '../../../../common/hooks/useUser'
import {EntityType, getEntityApiPath} from '../types'
import {fromEntitySlug, useSelectedScope} from '../utils'
import {useNavigatorScopes} from './useNavigatorScopes'

interface EntityLastEdit {
  lastEditDate: string | undefined
  lastEditAuthor: string | undefined
}

interface ConfigMeta {
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

interface ConfigMetaResponse {
  scopeId: string
  _meta?: ConfigMeta
}

/** Raw API shape — _meta may be at scope level (1:1) or nested in configurations (1:N) */
interface RawScopeResponse {
  scopeId: string
  _meta?: ConfigMeta
  configurations?: Array<{_meta?: ConfigMeta}>
}

/** Extract _meta from scope-level or from first configuration entry (1:N entities) */
const extractMeta = (item: RawScopeResponse): ConfigMetaResponse => {
  const meta = item._meta ?? item.configurations?.[0]?._meta
  return {scopeId: item.scopeId, _meta: meta}
}

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

export const useEntityLastEdit = (): EntityLastEdit => {
  const {countryId, businessLine, entityType: entitySlug} = useParams<{
    countryId: string
    businessLine: string
    entityType: string
  }>()
  const entityType = fromEntitySlug(entitySlug) as EntityType
  const {selectedScopeId} = useSelectedScope()
  const {rootScopeId} = useNavigatorScopes(entityType, countryId, businessLine)

  const effectiveScopeId = selectedScopeId ?? rootScopeId
  const queryClient = useQueryClient()

  const {data: configs} = useQuery<ConfigMetaResponse[]>({
    queryKey: ['entityLastEdit', entityType, countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<RawScopeResponse[]>(
        `/tuc-configs/${getEntityApiPath(entityType!)}`,
        {params: {countryId, businessLine: businessLine!.toUpperCase()}}
      )
      return (res.data ?? []).map(extractMeta)
    },
    enabled: Boolean(entityType && countryId && businessLine),
    staleTime: 30_000,
    initialData: () => {
      const cached = queryClient.getQueryData<RawScopeResponse[]>([`${entityType}Config`, countryId, businessLine])
      return cached?.map(extractMeta)
    },
    initialDataUpdatedAt: () => {
      return queryClient.getQueryState([`${entityType}Config`, countryId, businessLine])?.dataUpdatedAt
    },
  })

  const meta = useMemo(() => {
    if (!configs || !effectiveScopeId) return undefined
    const match = configs.find((c) => c.scopeId === effectiveScopeId)
    if (match) return match._meta
    // Fallback to root scope
    const root = rootScopeId ? configs.find((c) => c.scopeId === rootScopeId) : configs[0]
    return root?._meta
  }, [configs, effectiveScopeId, rootScopeId])

  const user = useUser(meta?.updatedBy)

  return useMemo(() => {
    if (!meta?.updatedAt) {
      return {lastEditDate: undefined, lastEditAuthor: undefined}
    }

    return {
      lastEditDate: dateFormatter.format(new Date(meta.updatedAt)),
      lastEditAuthor: user.data?.name ?? meta.updatedBy ?? undefined,
    }
  }, [meta, user.data?.name])
}
